import React, { useState, useEffect, useMemo } from 'react';
import { edefterAuthHeaders } from '../../services/edefterApi';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Paper,
  LinearProgress,
  Divider,
  IconButton,
  InputAdornment,
  Avatar,
  Autocomplete,
  Collapse,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Business,
  AccountBalance,
  PictureAsPdf,
  InsertDriveFile,
  CheckCircle,
  Error as ErrorIcon,
  Download,
  Visibility,
  Send,
  Refresh,
  CalendarToday,
  Info,
  Email,
  Phone,
  LocationOn,
  ExpandMore,
  ChevronRight,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import MizanService from '../../services/mizanService';
import CompanyService from '../../services/companyService';
import AuthService from '../../services/authService';

const EDEFTER_API_URL = window.APP_CONFIG?.edefterApiUrl || 'https://srv.dpg.net.tr/edefter';

const MizanReading = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [mizanData, setMizanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form fields
  const [companyId, setCompanyId] = useState('');
  const [taxId, setTaxId] = useState('');
  
  // Company list for selection
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  // Uploaded trial balances list
  const [uploadedMizans, setUploadedMizans] = useState([]);
  const [loadingMizans, setLoadingMizans] = useState(false);
  const [selectedMizanDetail, setSelectedMizanDetail] = useState(null);
  const [viewingMizanId, setViewingMizanId] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Açık olan account code'lar

  const currentUser = AuthService.getCurrentUser();

  // Fetch companies for current user
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!currentUser?.id) return;
      
      setLoadingCompanies(true);
      try {
        const result = await CompanyService.getCompaniesByUserId(currentUser.id);
        if (result.success) {
          setCompanies(result.data || []);
        }
      } catch (err) {
        console.error('Fetch companies error:', err);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [currentUser?.id]);

  // Update companyId and taxId when company is selected
  useEffect(() => {
    if (selectedCompany) {
      setCompanyId(selectedCompany.id);
      setTaxId(selectedCompany.vergiKimlikNo || '');
    }
  }, [selectedCompany]);

  // Fetch uploaded trial balances
  const fetchUploadedMizans = async () => {
    if (!companyId) return;
    
    setLoadingMizans(true);
    try {
      const result = await MizanService.getMizanBelgelerByCompany(companyId);
      if (result.success) {
        setUploadedMizans(result.data || []);
      } else {
        toast.error(result.error || 'Failed to load trial balance documents.');
      }
    } catch (err) {
      console.error('Fetch uploaded trial balances error:', err);
      toast.error('Failed to load trial balance documents.');
    } finally {
      setLoadingMizans(false);
    }
  };

  // Fetch trial balances when company ID changes
  useEffect(() => {
    if (companyId) {
      fetchUploadedMizans();
    } else {
      setUploadedMizans([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // View trial balance details
  const handleViewMizan = async (mizanId) => {
    setViewingMizanId(mizanId);
    setLoading(true);
    try {
      const result = await MizanService.getMizanById(mizanId);
      if (result.success) {
        setSelectedMizanDetail(result.data);
        setMizanData(result.data.mizanList || []);
      } else {
        toast.error(result.error || 'Failed to load trial balance details.');
      }
    } catch (err) {
      console.error('View trial balance error:', err);
      toast.error('Failed to load trial balance details.');
    } finally {
      setLoading(false);
    }
  };

  // Download trial balance
  const handleDownloadMizan = async (mizanId, timestamp) => {
    if (!companyId) {
      toast.warning('Please enter Company ID.');
      return;
    }
    
    setLoading(true);
    try {
      const result = await MizanService.downloadMizan(companyId, mizanId, timestamp);
      if (result.success && result.blob) {
        // Download blob
        const url = window.URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mizan_${mizanId}_${timestamp}.${result.blob.type.includes('pdf') ? 'pdf' : 'xlsx'}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Trial balance downloaded successfully.');
      } else {
        toast.error(result.error || 'Failed to download trial balance.');
      }
    } catch (err) {
      console.error('Download trial balance error:', err);
      toast.error('Failed to download trial balance.');
    } finally {
      setLoading(false);
    }
  };

  // Transfer trial balance
  const handleAktarMizan = async (mizanId) => {
    setLoading(true);
    try {
      const result = await MizanService.aktarMizan(mizanId);
      if (result.success) {
        toast.success('Trial balance transferred successfully.');
        // Refresh mizans
        if (companyId) {
          fetchUploadedMizans();
        }
      } else {
        toast.error(result.error || 'Failed to transfer trial balance.');
      }
    } catch (err) {
      console.error('Transfer trial balance error:', err);
      toast.error('Failed to transfer trial balance.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls', '.pdf'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        toast.error('Please select an Excel (.xlsx, .xls) or PDF (.pdf) file.');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      setSuccess(false);
      setMizanData(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.warning('Please select a file.');
      return;
    }

    if (!companyId || !taxId) {
      toast.warning('Please enter Company ID and Tax Number.');
      return;
    }

    if (!currentUser?.id) {
      toast.error('User information not found. Please login again.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
    setMizanData(null);

    let processingInterval = null;
    let currentProgressValue = 0;

    try {
      // Progress tracking - upload 0-70%
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const uploadPercent = Math.round((e.loaded / e.total) * 70);
          currentProgressValue = uploadPercent;
          setUploadProgress(uploadPercent);
        }
      });

      // Estimated progress for backend processing (70-100%)
      const startProcessingProgress = () => {
        currentProgressValue = 70;
        
        processingInterval = setInterval(() => {
          const increment = 1 + Math.random();
          currentProgressValue += increment;
          
          if (currentProgressValue >= 95) {
            setUploadProgress(95);
            clearInterval(processingInterval);
            processingInterval = null;
          } else {
            setUploadProgress(Math.round(currentProgressValue));
          }
        }, 150);
      };

      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            currentProgressValue = 70;
            setUploadProgress(70);
            startProcessingProgress();
            
            try {
              const responseData = JSON.parse(xhr.responseText);
              resolve(responseData);
            } catch (parseError) {
              resolve(xhr.responseText);
            }
          } else {
            if (processingInterval) clearInterval(processingInterval);
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData || 'Upload failed'));
            } catch {
              reject(new Error(xhr.statusText || 'Upload failed'));
            }
          }
        };
        
        xhr.onerror = () => {
          if (processingInterval) clearInterval(processingInterval);
          reject(new Error('Network error'));
        };

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('userId', currentUser.id);
        formData.append('companyId', companyId);
        formData.append('taxId', taxId);

        xhr.open('POST', `${EDEFTER_API_URL}/api/mizan/upload`);
        const _auth = edefterAuthHeaders();
        if (_auth.Authorization) xhr.setRequestHeader('Authorization', _auth.Authorization);
        xhr.send(formData);
      });

      // Wait for progress to increase during backend processing
      while (processingInterval && currentProgressValue < 95) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      if (processingInterval) {
        clearInterval(processingInterval);
      }
      
      setUploadProgress(100);
      await new Promise(resolve => setTimeout(resolve, 300));

      if (xhr.status === 409) {
        // Conflict - Trial balance already exists
        setError(result || 'This trial balance already exists in the database.');
        toast.error('This trial balance already exists in the database.');
      } else if (Array.isArray(result)) {
        setMizanData(result);
        setSuccess(true);
        toast.success('Trial balance uploaded and parsed successfully.');
        // Refresh uploaded trial balances
        if (companyId) {
          fetchUploadedMizans();
        }
      } else {
        setError('Unexpected response format.');
        toast.error('An error occurred while uploading trial balance.');
      }
    } catch (err) {
      console.error('Upload trial balance error:', err);
      setError(err.message || 'An error occurred while uploading mizan.');
      toast.error(err.message || 'An error occurred while uploading mizan.');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0.00';
    const number = parseFloat(value);
    if (isNaN(number)) return '0.00';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  // Mizan verilerini account code'a göre grupla
  // Hesap kodları prefix ilişkisine göre iç içe yapılır (örn: 101 ana, 101.2024 ve 101.2025 alt)
  const groupedMizanData = useMemo(() => {
    if (!mizanData || !Array.isArray(mizanData) || mizanData.length === 0) return null;

    const allAccountCodes = mizanData.map(row => row.hesapKodu || '').filter(code => code);
    const groups = new Map();
    const processedCodes = new Set();

    // Önce tüm satırları hesap koduna göre grupla
    mizanData.forEach((row) => {
      const accountCode = row.hesapKodu || '';
      if (!groups.has(accountCode)) {
        groups.set(accountCode, {
          accountCode,
          accountName: row.hesapAdi || '',
          baseRow: row,
          subRows: [],
        });
      } else {
        // Aynı hesap koduna sahip birden fazla satır varsa, diğerlerini subRow olarak ekle
        const group = groups.get(accountCode);
        group.subRows.push(row);
      }
    });

    // Şimdi prefix ilişkilerini bul ve alt kodları ana kodlara bağla
    allAccountCodes.forEach(code => {
      // Bu kod başka bir kodun prefix'i mi kontrol et (örn: "101" -> "101.2024"ün prefix'i)
      const hasChildren = allAccountCodes.some(otherCode => 
        otherCode !== code && otherCode.startsWith(code + '.')
      );

      if (hasChildren) {
        // Bu kod bir ana kod, alt kodlarını bul ve ekle
        const parentGroup = groups.get(code);
        if (parentGroup) {
          allAccountCodes.forEach(childCode => {
            if (childCode !== code && childCode.startsWith(code + '.')) {
              const childGroup = groups.get(childCode);
              if (childGroup && !processedCodes.has(childCode)) {
                // Alt kodun baseRow'unu parent'ın subRows'una ekle
                parentGroup.subRows.push(childGroup.baseRow);
                // Alt kodun kendi subRows'larını da ekle
                if (childGroup.subRows.length > 0) {
                  parentGroup.subRows.push(...childGroup.subRows);
                }
                processedCodes.add(childCode);
              }
            }
          });
        }
      }
    });

    // Ana grupları oluştur (işlenmemiş kodlar - yani alt kod olmayanlar)
    return Array.from(groups.entries())
      .filter(([code]) => !processedCodes.has(code))
      .map(([code, group]) => {
        const totalsFromSubRows = group.subRows.reduce(
          (acc, row) => {
            acc.borcToplami += parseFloat(row.borcToplami) || 0;
            acc.alacakToplami += parseFloat(row.alacakToplami) || 0;
            acc.borcBakiyesi += parseFloat(row.borcBakiyesi) || 0;
            acc.alacakBakiyesi += parseFloat(row.alacakBakiyesi) || 0;
            return acc;
          },
          { borcToplami: 0, alacakToplami: 0, borcBakiyesi: 0, alacakBakiyesi: 0 }
        );

        const hasMultipleDetails = group.subRows.length > 1;
        const hasAnyDetail = group.subRows.length > 0;

        return {
          ...group,
          totalBorcToplami: hasAnyDetail ? totalsFromSubRows.borcToplami : (group.baseRow?.borcToplami || 0),
          totalAlacakToplami: hasAnyDetail ? totalsFromSubRows.alacakToplami : (group.baseRow?.alacakToplami || 0),
          totalBorcBakiyesi: hasAnyDetail ? totalsFromSubRows.borcBakiyesi : (group.baseRow?.borcBakiyesi || 0),
          totalAlacakBakiyesi: hasAnyDetail ? totalsFromSubRows.alacakBakiyesi : (group.baseRow?.alacakBakiyesi || 0),
          hasMultipleDetails,
        };
      })
      .sort((a, b) => {
        return (a.accountCode || '').localeCompare(b.accountCode || '');
      });
  }, [mizanData]);

  const handleToggleRow = (accountCode) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(accountCode)) {
      newExpanded.delete(accountCode);
    } else {
      newExpanded.add(accountCode);
    }
    setExpandedRows(newExpanded);
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return <InsertDriveFile />;
    const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    if (extension === '.pdf') {
      return <PictureAsPdf sx={{ color: '#d32f2f' }} />;
    }
    return <Description sx={{ color: 'primary.main' }} />;
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 3,
            color: 'white'
          }}>
            {selectedCompany ? (
              <Box>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item>
                    <Avatar sx={{ 
                      width: 64, 
                      height: 64,
                      background: 'rgba(255,255,255,0.25)',
                      border: '3px solid rgba(255,255,255,0.3)'
                    }}>
                      <Business sx={{ fontSize: '1.5rem' }} />
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {selectedCompany.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Upload and analyze trial balance files in Excel or PDF format
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
                <Grid container spacing={3}>
                  {selectedCompany.vergiKimlikNo && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccountBalance sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Tax ID Number
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedCompany.vergiKimlikNo}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {selectedCompany.vergiDairesi && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccountBalance sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Tax Office
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedCompany.vergiDairesi}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {selectedCompany.email && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Email sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Email
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedCompany.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {selectedCompany.phone && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Phone sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Phone
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedCompany.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {selectedCompany.address && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocationOn sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Address
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedCompany.address}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Avatar sx={{ 
                    width: 64, 
                    height: 64,
                    background: 'rgba(255,255,255,0.25)',
                    border: '3px solid rgba(255,255,255,0.3)'
                  }}>
                    <AccountBalance sx={{ fontSize: '1.5rem' }} />
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Upload Trial Balance
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Upload and analyze trial balance files in Excel or PDF format
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>
            
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Company Information */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Business sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="600">
                  Company Information
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {/* Company Selection */}
                  <Grid item xs={12}>
                    <Autocomplete
                      options={companies}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.name || '';
                      }}
                      value={selectedCompany}
                      onChange={(event, newValue) => {
                        setSelectedCompany(newValue);
                        if (newValue) {
                          setCompanyId(newValue.id);
                          setTaxId(newValue.vergiKimlikNo || '');
                        } else {
                          setCompanyId('');
                          setTaxId('');
                        }
                      }}
                      loading={loadingCompanies}
                      sx={{ 
                        width: '100%',
                        display: 'block',
                        '& .MuiAutocomplete-root': {
                          width: '100%'
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          width: '100% !important',
                          minWidth: '100% !important',
                          flexWrap: 'nowrap'
                        },
                        '& .MuiAutocomplete-input': {
                          width: '100% !important',
                          minWidth: 0,
                          flex: '1 1 auto'
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Company"
                          variant="outlined"
                          required
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <Business color={selectedCompany ? 'primary' : 'disabled'} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: (
                              <>
                                {loadingCompanies ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {option.name}
                          </Typography>
                        </Box>
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* File Upload Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CloudUpload sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="600">
                  Trial Balance File (.xlsx, .xls, .pdf)
                </Typography>
              </Box>

              {!selectedFile ? (
                <Paper
                  sx={{
                    p: 4,
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 3,
                    textAlign: 'center',
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      borderColor: 'primary.dark',
                    },
                  }}
                  onClick={() => document.getElementById('mizan-file-input').click()}
                >
                  <input
                    id="mizan-file-input"
                    type="file"
                    accept=".xlsx,.xls,.pdf"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" color="primary" gutterBottom>
                    Upload Trial Balance File
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload trial balance file in Excel (.xlsx, .xls) or PDF (.pdf) format
                  </Typography>
                </Paper>
              ) : (
                <Box>
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <Paper
                      sx={{
                        p: 3,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        borderRadius: 3,
                        bgcolor: 'grey.50',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      {getFileIcon(selectedFile.name)}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={() => {
                          setSelectedFile(null);
                          setMizanData(null);
                          setError(null);
                          setSuccess(false);
                        }}
                        color="error"
                        size="small"
                      >
                        <ErrorIcon />
                      </IconButton>
                    </Paper>
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSubmit}
                    disabled={loading || !selectedFile || !companyId || !taxId}
                    sx={{
                      minWidth: 200,
                      py: 1.5,
                      color: 'white',
                      borderRadius: 3,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        color: 'white',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      }
                    }}
                  >
                    {loading ? `Uploading...` : 'Upload and Analyze'}
                  </Button>
                </Box>
                {loading && (
                  <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
                    <LinearProgress 
                      variant="determinate"
                      value={uploadProgress} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                        }
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      {uploadProgress < 70 
                        ? `Uploading file... ${uploadProgress}%` 
                        : uploadProgress < 100
                        ? `Processing... ${uploadProgress}%`
                        : 'Completed!'}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Success Message */}
              {success && !error && (
                <Box sx={{ mt: 3 }}>
                  <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                    Trial balance uploaded and parsed successfully.
                  </Alert>
                  {/* Transfer functionality temporarily disabled */}
                  {/* <Alert severity="info" icon={<Info />}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Next Steps:
                    </Typography>
                    <Typography variant="body2" component="div">
                      1. Review the trial balance data displayed below<br />
                      2. Click the <strong>Transfer</strong> button (Send icon) in the "Uploaded Trial Balance Files" section to transfer the data to Balance Sheet and Income Statement<br />
                      3. After transfer, the data will be available in financial reports
                    </Typography>
                  </Alert> */}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Uploaded Trial Balances List */}
            {companyId && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Uploaded Trial Balance Files
                  </Typography>
                  <Button
                    startIcon={<Refresh />}
                    onClick={fetchUploadedMizans}
                    disabled={loadingMizans}
                    size="small"
                    variant="outlined"
                  >
                    Refresh
                  </Button>
                </Box>

                {loadingMizans ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : uploadedMizans.length === 0 ? (
                  <Alert severity="info">No uploaded trial balance files found.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: 'rgba(0,0,0,0.04)' }}>
                            File Name
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: 'rgba(0,0,0,0.04)' }}>
                            Date Range
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: 'rgba(0,0,0,0.04)' }}>
                            Timestamp
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: 'rgba(0,0,0,0.04)' }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadedMizans.map((mizan, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              '&:nth-of-type(odd)': {
                                backgroundColor: 'rgba(0,0,0,0.02)',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.04)',
                              },
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {mizan.extension === 'pdf' ? (
                                  <PictureAsPdf sx={{ color: '#d32f2f', fontSize: 20 }} />
                                ) : (
                                  <Description sx={{ color: 'primary.main', fontSize: 20 }} />
                                )}
                                <Typography variant="body2">
                                  {mizan.fileName || mizan.generatedFileName || '-'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {mizan.tarihAraligi || '-'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {mizan.timestamp || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewMizan(mizan.mizanId)}
                                  title="View"
                                >
                                  <Visibility />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleDownloadMizan(mizan.mizanId, mizan.timestamp)}
                                  title="Download"
                                  disabled={loading}
                                >
                                  <Download />
                                </IconButton>
                                {/* Transfer button temporarily disabled - functionality not yet implemented */}
                                {/* <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleAktarMizan(mizan.mizanId)}
                                  title="Transfer"
                                  disabled={loading}
                                >
                                  <Send />
                                </IconButton> */}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Trial Balance Data Table */}
            {mizanData && mizanData.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Trial Balance Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total {mizanData.length} rows
                  </Typography>
                </Box>

                <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap', width: 50 }}>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Account Code
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Account Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Debit Total
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Credit Total
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Debit Balance
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Credit Balance
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedMizanData ? (
                        groupedMizanData.map((group, groupIndex) => {
                          const isExpanded = expandedRows.has(group.accountCode);
                          const hasMultipleDetails = group.hasMultipleDetails;
                          const hasAnyDetail = group.subRows.length > 0;

                          const renderSimpleRow = (rowData) => (
                            <TableRow
                              key={`group-simple-${group.accountCode}-${groupIndex}`}
                              hover
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                },
                                borderLeft: '4px solid transparent',
                              }}
                            >
                              <TableCell sx={{ width: 50 }} />
                              <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                {rowData.hesapKodu || group.accountCode}
                              </TableCell>
                              <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                {rowData.hesapAdi || group.accountName}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                {formatNumber(rowData.borcToplami ?? group.totalBorcToplami)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                {formatNumber(rowData.alacakToplami ?? group.totalAlacakToplami)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                {formatNumber(rowData.borcBakiyesi ?? group.totalBorcBakiyesi)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                {formatNumber(rowData.alacakBakiyesi ?? group.totalAlacakBakiyesi)}
                              </TableCell>
                            </TableRow>
                          );

                          if (!hasAnyDetail || (hasAnyDetail && !hasMultipleDetails)) {
                            const rowToRender = group.subRows[0] || group.baseRow || {
                              hesapKodu: group.accountCode,
                              hesapAdi: group.accountName,
                              borcToplami: group.totalBorcToplami,
                              alacakToplami: group.totalAlacakToplami,
                              borcBakiyesi: group.totalBorcBakiyesi,
                              alacakBakiyesi: group.totalAlacakBakiyesi,
                            };
                            return renderSimpleRow(rowToRender);
                          }

                          return (
                            <React.Fragment key={`group-${group.accountCode}-${groupIndex}`}>
                              {/* Ana satır (toplam) */}
                              <TableRow 
                                hover
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  },
                                  borderLeft: '4px solid transparent',
                                  backgroundColor: 'rgba(25, 118, 210, 0.02)'
                                }}
                              >
                                <TableCell sx={{ width: 50 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleRow(group.accountCode)}
                                    sx={{ p: 0.5 }}
                                  >
                                    {isExpanded ? (
                                      <ExpandMore sx={{ fontSize: 20 }} />
                                    ) : (
                                      <ChevronRight sx={{ fontSize: 20 }} />
                                    )}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                  {group.accountCode}
                                </TableCell>
                                <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem', fontWeight: 600 }}>
                                  {group.accountName}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                  {formatNumber(group.totalBorcToplami)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                  {formatNumber(group.totalAlacakToplami)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                  {formatNumber(group.totalBorcBakiyesi)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                  {formatNumber(group.totalAlacakBakiyesi)}
                                </TableCell>
                              </TableRow>
                              
                              {/* Detay satırları (Collapse içinde) */}
                              <TableRow>
                                <TableCell colSpan={7} sx={{ py: 0, border: 'none' }}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ margin: 0 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.05)' }}>
                                            <TableCell sx={{ width: 50 }} />
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                              Account Code
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                              Account Name
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                              Debit Total
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                              Credit Total
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                              Debit Balance
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                              Credit Balance
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {group.subRows.map((row, rowIndex) => (
                                            <TableRow 
                                              key={`detail-${group.accountCode}-${rowIndex}`}
                                              sx={{
                                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                '&:hover': {
                                                  backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                                },
                                              }}
                                            >
                                              <TableCell sx={{ width: 50, pl: 4 }}></TableCell>
                                              <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                                {row.hesapKodu || ''}
                                              </TableCell>
                                              <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                                {row.hesapAdi || ''}
                                              </TableCell>
                                              <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                {formatNumber(row.borcToplami)}
                                              </TableCell>
                                              <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                {formatNumber(row.alacakToplami)}
                                              </TableCell>
                                              <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                                {formatNumber(row.borcBakiyesi)}
                                              </TableCell>
                                              <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                                {formatNumber(row.alacakBakiyesi)}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })
                      ) : (
                        mizanData.map((row, index) => (
                          <TableRow
                            key={index}
                            hover
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              },
                            }}
                          >
                            <TableCell sx={{ width: 50 }} />
                            <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                              {row.hesapKodu || '-'}
                            </TableCell>
                            <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                              {row.hesapAdi || '-'}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                              {formatNumber(row.borcToplami)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                              {formatNumber(row.alacakToplami)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                              {formatNumber(row.borcBakiyesi)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                              {formatNumber(row.alacakBakiyesi)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default MizanReading;
